/* ============================================================================
   sheet-builder.jsx — The Sticker Lab "Smart Layout Generator"
   A calm sticker-sheet workspace. NOT a drag-and-drop editor.
   The customer uploads ONE design; the engine duplicates it across a
   production-ready 11" x 11" sheet automatically. Customers order stickers —
   Stickr Lab determines the optimal production layout (D-030). The finished
   size carries forward from the sticker they already built; the customer only
   chooses QUANTITY. Size, layout, spacing, margins and orientation are all
   production decisions and are never re-asked here.
   Exposes window.SheetBuilder — load AFTER React, BEFORE premium-builder.jsx.
   ========================================================================== */
(function () {
  const {
    useState,
    useMemo
  } = React;

  // Count-up: animates a number toward its target (easeOutCubic). Makes the
  // total-sticker figure feel like it's climbing as you pack more in.
  function useCountUp(value, ms) {
    const [n, setN] = useState(value);
    const from = React.useRef(value);
    React.useEffect(() => {
      if (typeof requestAnimationFrame === 'undefined') {
        setN(value);
        from.current = value;
        return;
      }
      const start = from.current,
        end = value,
        t0 = performance.now(),
        dur = ms || 600;
      if (start === end) return;
      let raf;
      const tick = t => {
        const p = Math.min(1, (t - t0) / dur);
        const e = 1 - Math.pow(1 - p, 3);
        setN(Math.round(start + (end - start) * e));
        if (p < 1) raf = requestAnimationFrame(tick);else from.current = end;
      };
      raf = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(raf);
    }, [value]);
    return n;
  }
  // Chosen-sheet model (D-050): the customer picks the finished SHEET size (shared catalog) and the
  // NUMBER OF SHEETS. Sheet size options, layout/yield, and eligibility all come from the shared
  // SLSheets engine — desktop and mobile read the SAME catalog (SLSheets.SHEET_SIZES). Sticker size
  // carries forward from the builder. Pricing is by total stickers delivered (SLPricing.sheet).

  const SHEET_QTYS = [5, 10, 25, 50]; // number-of-sheets quick chips

  function SheetBuilder({
    design,
    widthIn,
    heightIn,
    cutWidthIn,
    cutHeightIn,
    sheetSizeId,
    numSheets,
    onSheetSize,
    onNumSheets,
    onBack,
    onOrder
  }) {
    const artUrl = design && design.url || null;
    const artName = design && design.name || 'Your design';
    const isCircle = !!(design && design.circle);
    // Finished footprint carries forward from the individual sticker (D-030) — never re-asked.
    const fpW = +widthIn > 0 ? +(+widthIn).toFixed(2) : 3;
    const fpH = +heightIn > 0 ? +(+heightIn).toFixed(2) : 3;
    // Sheet size + number of sheets are CONTROLLED by the host (shared desktop/mobile state, D-050).
    const SHEET_SIZES = (typeof window !== 'undefined' && window.SLSheets && window.SLSheets.SHEET_SIZES) || [];
    const sizeId = sheetSizeId || (typeof window !== 'undefined' && window.SLSheets && window.SLSheets.DEFAULT_SHEET_SIZE_ID) || '8.5x10';
    /* SINGLE STICKER (D-198): one kiss-cut sticker on its own trimmed backing. "Sheets" are then
       stickers, sold from SLSheets.SINGLE_MIN_QTY (the die-cut price floor of 10). */
    const SL = typeof window !== 'undefined' && window.SLSheets || null;
    const single = !!(SL && SL.isSingle && SL.isSingle(sizeId));
    /* the size's minimum (10 sheets for 4 x 6, Ryan 24 Oct 2026); the host also raises the count
       to reach the 10-sticker pricing minimum once the layout is known */
    const minSheets = SL && SL.minSheets ? SL.minSheets(sizeId) : (single ? SL.SINGLE_MIN_QTY || 10 : 1);
    const qtyChips = single ? SL.SINGLE_QTYS || [10, 25, 50, 100] : SHEET_QTYS;
    const sheets = Math.max(minSheets, Math.round(+numSheets || 1));
    const setSize = id => { if (onSheetSize) onSheetSize(id); };
    const setSheets = n => { if (onNumSheets) onNumSheets(Math.max(minSheets, Math.round(n))); };
    const [showProd, setShowProd] = useState(false);
    const [qtyText, setQtyText] = useState(''); // editable number-of-sheets buffer
    const stageRef = React.useRef(null);
    const [stageBox, setStageBox] = useState({
      w: 360,
      h: 460
    }); // available stage box (px)
    React.useLayoutEffect(() => {
      const el = stageRef.current;
      if (!el || typeof ResizeObserver === 'undefined') return;
      const ro = new ResizeObserver(() => {
        const w = Math.max(140, el.clientWidth - 24),
          h = Math.max(180, el.clientHeight - 72); // room for the reassurance line + badge overflow
        setStageBox({
          w: w,
          h: h
        });
      });
      ro.observe(el);
      return () => ro.disconnect();
    }, []);

    // Layout / yield / eligibility come from the shared SLSheets engine in CHOSEN-SHEET mode (D-050):
    // the customer's sheet size + number of sheets are the inputs; per-sheet, total, cols/rows all derive.
    const fp = {
      w: fpW,
      h: fpH
    }; // finished sticker footprint, carried from the builder (D-030)
    const plan = useMemo(() => typeof window !== 'undefined' && window.SLSheets ? window.SLSheets.layout(fp.w, fp.h, 0, { sheet: sizeId, sheetQuantity: sheets, cutWidth: cutWidthIn, cutHeight: cutHeightIn }) : null, [fp.w, fp.h, sizeId, sheets, cutWidthIn, cutHeightIn]);
    const perSheet = plan && plan.stickersPerSheet || 0;
    const cols = plan && plan.columns || 1;
    const rows = plan && plan.rows || 1;
    const eligible = !!(plan && plan.status === 'ok' && (plan.stickersPerSheet > 1 || plan.single)); // fits + >1 per sheet, or one backed sticker (D-198)
    const fits = !!(plan && plan.status === 'ok'); // sticker fits the chosen sheet
    const sheetMsg = plan && plan.messaging || null; // informational copy (sheets · per-sheet · total)
    const GAP_IN = typeof window !== 'undefined' && window.SLSheets ? window.SLSheets.constants.GAP_IN : 0.12;

    // Quantity model (D-050): the customer buys whole SHEETS. Total stickers = perSheet × sheets.
    const stepSheets = d => setSheets(sheets + d);
    const totalStickers = plan && plan.totalStickers != null ? plan.totalStickers : perSheet * sheets;
    const shownTotal = useCountUp(totalStickers);
    const shownSheets = useCountUp(sheets, 420); // the customer-facing deliverable

    // ---- sheet preview geometry (px) — frame the preview to the TRIMMED sheet (the actual
    // deliverable, ≤ the 8.5×10 envelope — D-031) so it fills the canvas. Presentation only:
    // every count/size/dimension/orientation comes from the engine (D-024/D-030/D-031).
    const rotated = !!(plan && plan.orientation === 'rotated-90'); // engine rotated the sticker to pack more
    // Use the engine's NORMALIZED size (snapped to 0.25") so the preview cells match the
    // engine's trimmed calc exactly — otherwise the outer margins wouldn't read as 0.30".
    const normW = plan && plan.normalizedWidth || fp.w;
    const normH = plan && plan.normalizedHeight || fp.h;
    const footW = rotated ? normH : normW; // on-sheet footprint (the sticker as placed)
    const footH = rotated ? normW : normH;
    /* THE FINISHED SHEET IS WHAT IS DRAWN (D-179). The size the customer picked is the
       NOMINAL sheet: it decides how many fit. What they receive is trimmed closer to the
       stickers, and the engine says exactly how: finished width and height, where each
       sticker sits, and the corner radius. Nothing here recomputes any of it. */
    const fin = plan && plan.finished || null;
    const nomW = plan && plan.trimmedWidth || footW + 2 * 0.30;   // nominal (the chosen size)
    const nomH = plan && plan.trimmedHeight || footH + 2 * 0.30;
    const trimW = fin ? fin.widthIn : nomW;
    const trimH = fin ? fin.heightIn : nomH;
    // Anchor ONE px/inch scale to the MAX ENVELOPE (D-031 fix): the preview then shows the
    // finished sheet's TRUE relative size — a small trimmed sheet renders small, and only a
    // sheet that actually needs the full envelope fills the stage. The envelope is a
    // production constraint, not the preview size. Artwork is never scaled independently.
    const ENV_W = typeof window !== 'undefined' && window.SLSheets ? window.SLSheets.constants.ENVELOPE_W_IN : 8.5;
    const ENV_H = typeof window !== 'undefined' && window.SLSheets ? window.SLSheets.constants.ENVELOPE_H_IN : 10;
    const dispScale = Math.min(90, Math.min(stageBox.w / ENV_W, stageBox.h / ENV_H)); // px per inch; envelope fits the stage
    const sheetW = trimW * dispScale,
      sheetH = trimH * dispScale;
    const cellW = (fin ? fin.stickerWidthIn : footW) * dispScale;   // the REAL sticker, as placed
    const cellH = (fin ? fin.stickerHeightIn : footH) * dispScale;
    const cornerPx = (fin ? fin.cornerRadiusIn : 0.11) * dispScale;
    const gapPx = GAP_IN * dispScale;
    const gridW = cols * cellW + (cols - 1) * gapPx;
    const gridH = rows * cellH + (rows - 1) * gapPx;
    const offX = (sheetW - gridW) / 2; // center the block; the remainder is the trim border
    const offY = (sheetH - gridH) / 2;
    const cells = [];
    if (fin) fin.stickers.forEach((st, i) => cells.push({
      key: Math.floor(i / cols) + '-' + (i % cols),
      x: st.xIn * dispScale,
      y: st.yIn * dispScale
    }));
    else for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
      cells.push({
        key: r + '-' + c,
        x: offX + c * (cellW + gapPx),
        y: offY + r * (cellH + gapPx)
      });
    }

    // Sticker Sheet price = die-cut × 0.92 (D-033), DERIVED from SLPricing — single source of
    // truth, no separate table. Available for the die-cut engine's supported quantities (10–1000).
    // Price by TOTAL stickers delivered (D-050/D-033) — one engine, no sheet-count pricing table.
    const priceInfo = useMemo(() => typeof window !== 'undefined' && window.SLPricing && window.SLPricing.sheet ? (single ? window.SLPricing.diecut(fp.w, fp.h, totalStickers) : window.SLPricing.sheet(fp.w, fp.h, totalStickers)) : null, [fp.w, fp.h, totalStickers, single]); // a single sticker is priced as a regular die-cut (D-198)
    const priced = !!(priceInfo && !priceInfo.error && eligible);
    const perSheetPrice = priced ? Math.round(priceInfo.price / sheets * 100) / 100 : null; // display only
    // The enabled CTA adds to the cart via the host onOrder callback, which builds the shared sheet
    // payload (SLBuilderCore) and submits through SLCartAdapter — same transport as die-cut (D-050).
    const order = () => {
      if (!priced || !onOrder) return;
      onOrder({
        sheetSizeId: sizeId,
        numSheets: sheets,
        totalStickers: totalStickers,
        perSheet: perSheet,
        sheets: sheets,
        size: fpW + ' × ' + fpH,
        layout: perSheet + '/sheet',
        thumb: artUrl,
        unit: perSheetPrice,
        total: priceInfo.price,
        perSticker: priceInfo.perUnit
      });
    };
    return /*#__PURE__*/React.createElement("div", {
      className: "sb"
    }, /*#__PURE__*/React.createElement("div", {
      className: "sb-top"
    }, /*#__PURE__*/React.createElement("button", {
      className: "sb-back",
      onClick: onBack
    }, /*#__PURE__*/React.createElement("svg", {
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2.2",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M15 6l-6 6 6 6"
    })), "Back to sticker"), /*#__PURE__*/React.createElement("div", {
      className: "sb-title"
    }, /*#__PURE__*/React.createElement("b", null, "Sticker Sheet"), /*#__PURE__*/React.createElement("span", null, "Professionally arranged by Stickr Lab")), /*#__PURE__*/React.createElement("div", {
      className: "sb-top-sp"
    }), /*#__PURE__*/React.createElement("span", {
      className: "sb-badge"
    }, /*#__PURE__*/React.createElement("svg", {
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M12 2v4M12 18v4M2 12h4M18 12h4M4.9 4.9l2.8 2.8M16.3 16.3l2.8 2.8M19.1 4.9l-2.8 2.8M7.7 16.3l-2.8 2.8"
    })), "Professionally arranged by Stickr Lab")), /*#__PURE__*/React.createElement("div", {
      className: "sb-body"
    }, /*#__PURE__*/React.createElement("div", {
      className: "sb-stage",
      ref: stageRef
    }, /*#__PURE__*/React.createElement("div", {
      className: "sb-sheetwrap"
    }, /*#__PURE__*/React.createElement("div", {
      className: "sb-sheet",
      style: {
        width: sheetW + 'px',
        height: sheetH + 'px',
        '--sb-corner': cornerPx + 'px'
      }
    }, /*#__PURE__*/React.createElement("span", {
      className: "sb-sheet-tag"
    }, "Production preview"), artUrl ? cells.map(cell => /*#__PURE__*/React.createElement("div", {
      key: cell.key,
      className: 'sb-cell' + (isCircle ? ' circle' : ''),
      style: {
        left: cell.x + 'px',
        top: cell.y + 'px',
        width: cellW + 'px',
        height: cellH + 'px'
      }
    }, /*#__PURE__*/React.createElement("img", {
      src: artUrl,
      alt: "",
      style: rotated ? {
        position: 'absolute',
        left: '50%',
        top: '50%',
        maxWidth: 'none',
        maxHeight: 'none',
        width: cellH + 'px', /* 100%: the cell IS the sticker's cut box (SB-2, 24 Oct 2026: 94% drew it 6% small) */
        height: cellW + 'px',
        objectFit: 'contain',
        transform: 'translate(-50%,-50%) rotate(90deg)'
      } : {
        maxWidth: 'none',
        maxHeight: 'none',
        width: '100%',
        height: '100%',
        objectFit: 'contain'
      }
    }))) : /*#__PURE__*/React.createElement("div", {
      style: {
        position: 'absolute',
        inset: 0,
        display: 'grid',
        placeItems: 'center'
      },
      className: "sb-empty-note"
    }, "Upload a design to fill the sheet")), /*#__PURE__*/React.createElement("div", {
      className: "sb-reassure"
    }, artUrl && /*#__PURE__*/React.createElement("div", {
      className: "sb-packbadge"
    }, /*#__PURE__*/React.createElement("span", {
      className: "sb-pb-num",
      key: 's' + sheets
    }, shownSheets.toLocaleString()), /*#__PURE__*/React.createElement("span", {
      className: "sb-pb-tx"
    }, /*#__PURE__*/React.createElement("b", null, single ? "Backed" : "Trimmed"), /*#__PURE__*/React.createElement("small", null, single ? "Stickers" : "Sheets"))), /*#__PURE__*/React.createElement("span", {
      className: "sb-eff hi"
    }, /*#__PURE__*/React.createElement("svg", {
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2.4",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M20 6 9 17l-5-5"
    })), "Professionally arranged by Stickr Lab")))), /*#__PURE__*/React.createElement("div", {
      className: "sb-right"
    }, /*#__PURE__*/React.createElement("div", {
      className: "sb-ctrl"
    }, /*#__PURE__*/React.createElement("div", {
      className: "sb-designchip"
    }, /*#__PURE__*/React.createElement("div", {
      className: "sb-dc-thumb"
    }, artUrl && /*#__PURE__*/React.createElement("img", {
      src: artUrl,
      alt: ""
    })), /*#__PURE__*/React.createElement("div", {
      className: "sb-dc-meta"
    }, /*#__PURE__*/React.createElement("small", null, "Your design"), /*#__PURE__*/React.createElement("b", null, artName)), /*#__PURE__*/React.createElement("button", {
      className: "sb-dc-change",
      onClick: onBack
    }, "Change")), /*#__PURE__*/React.createElement("div", {
      className: "sb-sizecarry"
    }, /*#__PURE__*/React.createElement("span", null, "Sticker size"), /*#__PURE__*/React.createElement("b", null, fpW, "″ × ", fpH, "″")), /*#__PURE__*/React.createElement("div", {
      className: "sb-field"
    }, /*#__PURE__*/React.createElement("h4", null, "Sheet size"), /*#__PURE__*/React.createElement("div", {
      className: "sb-qty sb-sizes"
    }, SHEET_SIZES.map(s => /*#__PURE__*/React.createElement("button", {
      key: s.id,
      className: 'sb-qchip' + (sizeId === s.id ? ' on' : ''),
      onClick: () => setSize(s.id)
    }, s.label)))), /*#__PURE__*/React.createElement("div", {
      className: "sb-field"
    }, /*#__PURE__*/React.createElement("h4", null, single ? "Number of stickers" : "Number of sheets"), /*#__PURE__*/React.createElement("div", {
      className: "sb-qtybar"
    }, /*#__PURE__*/React.createElement("button", {
      className: "sb-step",
      disabled: sheets <= minSheets,
      onClick: () => stepSheets(-1),
      "aria-label": single ? "Fewer stickers" : "Fewer sheets"
    }, "−"), /*#__PURE__*/React.createElement("input", {
      className: "sb-qtyinput",
      type: "text",
      inputMode: "numeric",
      value: qtyText !== '' ? qtyText : String(sheets),
      onFocus: () => setQtyText(String(sheets)),
      onChange: e => {
        const v = e.target.value.replace(/[^0-9]/g, '');
        setQtyText(v);
        if (v) setSheets(parseInt(v, 10));
      },
      onBlur: () => setQtyText('')
    }), /*#__PURE__*/React.createElement("button", {
      className: "sb-step",
      onClick: () => stepSheets(1),
      "aria-label": single ? "More stickers" : "More sheets"
    }, "+")), /*#__PURE__*/React.createElement("div", {
      className: "sb-qty"
    }, qtyChips.map(q => /*#__PURE__*/React.createElement("button", {
      key: q,
      className: 'sb-qchip' + (sheets === q ? ' on' : ''),
      onClick: () => setSheets(q)
    }, q))), /*#__PURE__*/React.createElement("div", {
      className: "sb-perline"
    }, /*#__PURE__*/React.createElement("span", null, single ? "Each sticker on its own backing" : perSheet + " stickers per sheet"), perSheetPrice != null && /*#__PURE__*/React.createElement("span", null, "$", perSheetPrice.toFixed(2), single ? " per sticker" : " per sheet")))), /*#__PURE__*/React.createElement("div", {
      className: "sb-foot"
    }, /*#__PURE__*/React.createElement("div", {
      className: "sb-total-hero"
    }, /*#__PURE__*/React.createElement("div", {
      className: "sb-total-num"
    }, /*#__PURE__*/React.createElement("b", null, shownTotal.toLocaleString()), /*#__PURE__*/React.createElement("span", null, "Total Stickers"))), eligible && sheetMsg && /*#__PURE__*/React.createElement("div", {
      className: "sb-summary"
    }, /*#__PURE__*/React.createElement("svg", {
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.8",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M12 2 2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"
    })), /*#__PURE__*/React.createElement("p", null, /*#__PURE__*/React.createElement("b", null, sheetMsg.line1), /*#__PURE__*/React.createElement("span", null, sheetMsg.line2))), !eligible && /*#__PURE__*/React.createElement("div", {
      className: "sb-sumrows"
    }, /*#__PURE__*/React.createElement("div", {
      className: "sb-sr"
    }, /*#__PURE__*/React.createElement("span", null, fits ? "Only one per" : "Doesn't fit"), /*#__PURE__*/React.createElement("b", null, fits ? "sheet" : "this sheet")), /*#__PURE__*/React.createElement("div", {
      className: "sb-sr"
    }, /*#__PURE__*/React.createElement("span", null), /*#__PURE__*/React.createElement("span", null, fits ? "At this sticker size only one sticker fits on this sheet. Choose a larger sheet, or a smaller sticker, for more per sheet." : "This sticker is too large for the selected sheet. Choose a larger sheet, or a smaller sticker."))), /*#__PURE__*/React.createElement("div", {
      className: "sb-sumrows"
    }, /*#__PURE__*/React.createElement("div", {
      className: "sb-sr"
    }, /*#__PURE__*/React.createElement("span", null, "Per sticker"), /*#__PURE__*/React.createElement("b", null, priced ? priceInfo.perUnitText : '—')), priced && !single && /*#__PURE__*/React.createElement("div", {
      className: "sb-sr"
    }, /*#__PURE__*/React.createElement("span", null, "vs. individual die-cut"), /*#__PURE__*/React.createElement("b", null, "8% less")), /*#__PURE__*/React.createElement("div", {
      className: "sb-sr sb-sr-total"
    }, /*#__PURE__*/React.createElement("span", null, single ? "Price" : "Sheet price"), /*#__PURE__*/React.createElement("b", null, priced ? priceInfo.priceText : eligible ? 'Unavailable' : '—'))), /*#__PURE__*/React.createElement("div", {
      className: "sb-readyline"
    }, /*#__PURE__*/React.createElement("svg", {
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2.4",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M20 6 9 17l-5-5"
    })), "Ready to print", /*#__PURE__*/React.createElement("button", {
      className: "sb-proddet",
      onClick: () => setShowProd(v => !v)
    }, showProd ? 'Hide' : 'View', " production details")), showProd && /*#__PURE__*/React.createElement("div", {
      className: "sb-prodpanel"
    }, /*#__PURE__*/React.createElement("div", {
      className: "sb-sr"
    }, /*#__PURE__*/React.createElement("span", null, single ? "Backed stickers" : "Production sheets"), /*#__PURE__*/React.createElement("b", null, sheets)), /*#__PURE__*/React.createElement("div", {
      className: "sb-sr"
    }, /*#__PURE__*/React.createElement("span", null, single ? "Stickers per backing" : "Stickers per sheet"), /*#__PURE__*/React.createElement("b", null, perSheet)), /*#__PURE__*/React.createElement("div", {
      className: "sb-sr"
    }, /*#__PURE__*/React.createElement("span", null, "Sheet size"), /*#__PURE__*/React.createElement("b", null, single ? "Single sticker" : nomW + "″ × " + nomH + "″")), fin && /*#__PURE__*/React.createElement("div", {
      className: "sb-sr"
    }, /*#__PURE__*/React.createElement("span", null, single ? "Backing size (finished)" : "Finished size (trimmed)"), /*#__PURE__*/React.createElement("b", null, fin.widthIn.toFixed(2), "″ × ", fin.heightIn.toFixed(2), "″")), plan && plan.partialSheetStickerCount > 0 && plan.partialTrimmedWidth != null && /*#__PURE__*/React.createElement("div", {
      className: "sb-sr"
    }, /*#__PURE__*/React.createElement("span", null, "Remainder sheet"), /*#__PURE__*/React.createElement("b", null, plan.partialTrimmedWidth, "″ × ", plan.partialTrimmedHeight, "″ · ", plan.partialSheetStickerCount, " left")), /*#__PURE__*/React.createElement("div", {
      className: "sb-sr"
    }, /*#__PURE__*/React.createElement("span", null, "Cut style"), /*#__PURE__*/React.createElement("b", null, "Kiss-cut"))), /*#__PURE__*/React.createElement("button", {
      className: "sb-order",
      onClick: order,
      disabled: !priced
    }, /*#__PURE__*/React.createElement("svg", {
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2.2",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M20 6 9 17l-5-5"
    })), priced ? 'Add to cart · ' + priceInfo.priceText : !fits ? 'Choose a larger sheet' : !eligible ? 'Choose a larger sheet for more per sheet' : 'Sheet price unavailable'), /*#__PURE__*/React.createElement("p", {
      className: "sb-foot-note"
    }, "Want a free digital proof? Tick “Add a free digital proof” in your cart (free account needed).")))));
  }
  window.SheetBuilder = SheetBuilder;
})();
